
/**
 * @fileoverview Hook compartido useTemplateText para calcular texto de plantilla con variables dinámicas reemplazadas.
 * Permite procesar el texto de una plantilla, reemplazando tokens dinámicos por valores proporcionados en tiempo real.
 * Optimizado con useCallback y compatibilidad con botones CTA.
 *
 * @module useTemplateText
 * @category Hook - Shared
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa, optimización y compatibilidad CTA.
 */

/**
 * @typedef {Object} FieldValuesType
 * @description Estructura de valores dinámicos para reemplazo en plantilla.
 * @property {string} [variable-1] - Valor para la variable {{1}}
 * @property {string} [variable-2] - Valor para la variable {{2}}
 * @property {string} [variable-N] - Valor para la variable {{N}}
 */

/**
 * Hook compartido para calcular texto de plantilla con variables dinámicas reemplazadas.
 *
 * Implementa el patrón de **Hook Compartido** con separación estricta de responsabilidades:
 * - Solo procesa texto de plantilla y reemplazo de variables
 * - No gestiona estado de UI ni lógica de negocio
 * - Optimizado con useCallback para performance
 *
 * @function useTemplateText
 * @category Hook - Shared
 *
 * @param {Object} selectedTemplate - Objeto de plantilla seleccionada (debe contener propiedad 'text')
 *
 * @returns {function(FieldValuesType): string} Función para calcular texto procesado con valores dinámicos
 *
 * @example
 * const calculateTemplateText = useTemplateText(selectedTemplate);
 * const texto = calculateTemplateText({ 'variable-1': 'Juan', 'variable-2': 'Pérez' });
 *
 * @see {@link cloneDeep} Utilidad para clonar texto de plantilla
 *
 * @since 1.0.0
 * @version 1.1.0 - Documentación JSDoc completa y optimización
 */

import { useCallback } from 'react';
import { cloneDeep } from 'lodash';


const useTemplateText = (selectedTemplate) => {

  /**
   * Extrae el ID de variable de un token dinámico.
   * @function getId
   * @memberof useTemplateText
   * @private
   * @param {string} key - Token dinámico (ej: '{{1}}')
   * @returns {string} ID extraído (ej: '1')
   */
  const getId = useCallback((key) => key.replaceAll('{{', '').replaceAll('}}', ''), []);

  /**
   * Calcula el texto procesado de la plantilla, reemplazando variables dinámicas por valores.
   * @function calculateTemplateText
   * @memberof useTemplateText
   * @param {FieldValuesType} fieldValues - Valores dinámicos para reemplazo
   * @returns {string} Texto procesado con variables reemplazadas
   *
   * @example
   * const texto = calculateTemplateText({ 'variable-1': 'Juan' });
   */
  const calculateTemplateText = useCallback((fieldValues = {}) => {
    // Validación defensiva
    if (!selectedTemplate?.text) return '';

    const newText = cloneDeep(selectedTemplate.text);
    // Procesar botones CTA si existen (compatibilidad)
    const regex = /\[([^\]]+)\]/g;
    let match;
    const resultado = [];

    while ((match = regex.exec(newText)) !== null) {
      resultado.push(match[1]);
    }

    // Dividir texto por patrones de variables dinámicas
    const textArray = newText.split(/({{[1-9]+[0-9]*}})/g);
    if (!textArray) {
      return '';
    }

    let processedText = '';
    // Procesar cada segmento del texto
    textArray.forEach((segment) => {
      if (/{{[1-9]+[0-9]*}}/.test(segment)) {
        // Es una variable dinámica - reemplazar por valor
        const id = getId(segment);
        const fieldName = `variable-${id}`;
        processedText += fieldValues[fieldName] || ' ';
      } else {
        // Es texto estático - mantener sin cambios
        processedText += segment;
      }
    });
    return processedText;
  }, [selectedTemplate, getId]);

  return calculateTemplateText;
};

export default useTemplateText;